import React from 'react'
import { useTranslation } from 'react-i18next'
import { SettingsNestedMenu } from 'src/editor/layout/sidebar/settings/settings-nested-menu'
import {
  FormSubentityTypeEnum,
  FormInterface,
} from 'src/shared/types/entities/form-interface'
import { getFormSubentitiesSettingsIcons } from '../../utils/get-form-subentities-settings-icons'
import { FormFieldSubentitySettings } from './form-subentity-field-settings'
import { FormCheckboxSubentitySettings } from './form-subentity-checkbox-settings'
import { FormSubmitButtonSubentitySettings } from './form-subentity-submit-button-settings'
import { CheckSquareIcon } from 'src/editor/shared/icons/check-square-icon'
import { Share02Icon } from 'src/editor/shared/icons/share-02-icon'
import { getContactSlugName } from 'src/editor/shared/utils/get-contact-field-title'
import { PlusIcon } from 'src/editor/shared/icons/plus-icon'
import { FormAddNewSubentitySettings } from './form-add-new-subentity-settings'
import { FormSubentitiesSettingsNestedMenuItem } from './form-subentities-settings-nested-menu-item'
import { useUpdateEntity } from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import { useManagement } from 'client/store'
import { getSettingsEditing } from 'client/reducers/managementReducer'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import { setSettingsEditing } from 'client/store/management/managementActions'
import { FormSubentityDndItem } from './form-subentity-dnd-item'

interface FormSubentitiesSettingsProps {
  entity: FormInterface
}

export const FormSubentitiesSettings = ({
  entity,
}: FormSubentitiesSettingsProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const updateEntity = useUpdateEntity<FormInterface>()

  const editingSubentityId = useManagement(getSettingsEditing).subentityId

  const onClose = () => {
    dispatch(
      setSettingsEditing({
        id: entity.id,
        type: entity.type,
        subentityId: undefined,
      }),
    )
  }

  const onDelete = (id: string) => (e: React.MouseEvent<SVGSVGElement>) => {
    e.stopPropagation()
    updateEntity({
      ...entity,
      subEntities: entity.subEntities.filter(subentity => subentity.id !== id),
    })
  }

  const onMove = (dragIndex: number, hoverIndex: number) => {
    updateEntity({
      ...entity,
      subEntities: entity.subEntities.map((subentity, index) =>
        index === dragIndex
          ? entity.subEntities[hoverIndex]
          : index === hoverIndex
            ? entity.subEntities[dragIndex]
            : subentity,
      ),
    })
  }

  return (
    <div className="flex flex-col gap-2">
      {entity.subEntities.map((subentity, index) => (
        <>
          {subentity.type === FormSubentityTypeEnum.SubmitButton ? (
            <div className="flex items-center gap-2">
              <div className="w-5" />
              <FormSubentitiesSettingsNestedMenuItem
                title={t('entity_settings.submit_button.header')}
                icon={Share02Icon}
                isOpen={
                  editingSubentityId === undefined
                    ? undefined
                    : editingSubentityId === subentity.id
                }
                onClose={onClose}
              >
                <FormSubmitButtonSubentitySettings
                  formEntity={entity}
                  submitButtonSubentity={subentity}
                />
              </FormSubentitiesSettingsNestedMenuItem>
            </div>
          ) : (
            <FormSubentityDndItem
              key={subentity.id}
              id={subentity.id}
              index={index}
              onMove={onMove}
            >
              {subentity.type === FormSubentityTypeEnum.Field ? (
                <FormSubentitiesSettingsNestedMenuItem
                  title={getContactSlugName(subentity.slug, t)}
                  icon={getFormSubentitiesSettingsIcons(subentity.slug)}
                  onDelete={onDelete(subentity.id)}
                  isOpen={
                    editingSubentityId === undefined
                      ? undefined
                      : editingSubentityId === subentity.id
                  }
                  onClose={onClose}
                >
                  <FormFieldSubentitySettings
                    formEntity={entity}
                    fieldSubentity={subentity}
                  />
                </FormSubentitiesSettingsNestedMenuItem>
              ) : (
                subentity.type === FormSubentityTypeEnum.Checkbox && (
                  <FormSubentitiesSettingsNestedMenuItem
                    title={t('entity_settings.checkbox.header')}
                    icon={CheckSquareIcon}
                    onDelete={onDelete(subentity.id)}
                    isOpen={
                      editingSubentityId === undefined
                        ? undefined
                        : editingSubentityId === subentity.id
                    }
                    onClose={onClose}
                  >
                    <FormCheckboxSubentitySettings
                      formEntity={entity}
                      checkboxSubentity={subentity}
                    />
                  </FormSubentitiesSettingsNestedMenuItem>
                )
              )}
            </FormSubentityDndItem>
          )}
        </>
      ))}
      <SettingsNestedMenu
        withHorizontalLine
        className="m-0"
        menuTitle={t('entity_settings.add_new_element.header')}
        menuButtonClassName="p-0 mb-2"
        menuButtonIcon={() => <PlusIcon className="h-4 w-4" />}
        menuButtonLabel={t('entity_settings.add_new_element.header')}
      >
        <FormAddNewSubentitySettings entity={entity} />
      </SettingsNestedMenu>
    </div>
  )
}
